import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { audioCardStyles } from './styles';

// Import state components
import { InitialState } from './states/InitialState';
import { RecordingStateComponent } from './states/RecordingState';
import { StoppedState } from './states/StoppedState';

// Import hooks
import { useNote } from '@/context/NoteProvider';
import { useSimpleAudio } from '@/context/SimpleAudioProvider';
import { formatDuration } from '@/helpers/formatTime';
import { modifyBlobToWav } from '@/helpers/withLib';
import { useAudioPlayback } from './hooks/useAudioPlayback';
import { useRecordingState } from './hooks/useRecordingState';
import { useSilenceDetection } from './hooks/useSilenceDetection';

// Import components
import { SilenceWarningPopup } from './SilenceWarningPopup';

interface AudioStartCardProps {
  title: string;
  subtitle: string;
  allowParallelRecording?: boolean;
}

const AudioStartCard: React.FC<AudioStartCardProps> = ({
  title,
  subtitle,
  allowParallelRecording = false,
}) => {
  // Local state
  const [audioSrc, setAudioSrc] = useState('');
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);

  // Custom hooks
  const recording = useRecordingState();
  const audioPlayback = useAudioPlayback();
  const noteContext = useNote();
  const { setCurrentSessionNoteId, setShouldRefreshHistory } = useSimpleAudio();

  const silenceDetection = useSilenceDetection({
    volumeLevel: recording.volumeLevel,
    isRecording: recording.recordingState === 'recording',
  });

  // Process recorded audio when recording stops
  const processRecordedAudio = useCallback(async (recordingBlob: Blob) => {
    // Clean up previous audio URL
    setAudioSrc(prevAudioSrc => {
      if (prevAudioSrc) URL.revokeObjectURL(prevAudioSrc);
      return '';
    });

    setIsProcessingAudio(true);
    let finalBlob = recordingBlob;
    try {
      // Convert to WAV for consistent backend processing
      finalBlob = await modifyBlobToWav(recordingBlob);
    } catch (e) {
      // Graceful fallback to original blob if conversion fails
      Sentry.logger.warn(
        'WAV conversion failed, falling back to original blob',
        {
          error: String(e),
          originalSize: recordingBlob.size,
          originalType: recordingBlob.type,
        }
      );
    }

    const url = URL.createObjectURL(finalBlob);
    setAudioSrc(url);
    recording.setRecordedBlob(finalBlob);
    audioPlayback.createAudioElement(url);
    setIsProcessingAudio(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - function uses stable methods

  // Process recorded audio when recording stops - but avoid infinite loop
  useEffect(() => {
    if (recording.recordingBlob && recording.recordingState === 'stopped') {
      processRecordedAudio(recording.recordingBlob);
    }
  }, [recording.recordingBlob, recording.recordingState, processRecordedAudio]); // processRecordedAudio is stable now

  const handleStartRecording = async () => {
    // For non-sb-connect users, don't allow recording if a session note is being created/loaded
    if (noteContext.isSessionLoading && !allowParallelRecording) {
      return;
    }

    if (
      recording.recordingState === 'idle' ||
      recording.recordingState === 'stopped'
    ) {
      await Sentry.startSpan(
        {
          op: 'ui.action',
          name: 'Start Recording',
        },
        async span => {
          try {
            audioPlayback.cleanup();
            await recording.startRecording();
            noteContext.resetSession();
            span.setAttribute('success', true);
          } catch (error) {
            span.setAttribute('success', false);
            Sentry.captureException(error, {
              level: 'error',
              tags: { operation: 'start_recording' },
            });
            throw error;
          }
        }
      );
    }
  };

  const handleRecordNew = () => {
    audioPlayback.cleanup();
    recording.recordNew();
    setAudioSrc('');
    if (audioSrc) {
      URL.revokeObjectURL(audioSrc);
    }
    noteContext.resetSession();
  };

  const handleStopRecording = () => {
    Sentry.startSpan(
      {
        op: 'ui.action',
        name: 'Stop Recording',
      },
      span => {
        try {
          recording.stopRecording();
          span.setAttribute('recording_duration', recording.recordingTime);
          span.setAttribute('success', true);
        } catch (error) {
          span.setAttribute('success', false);
          Sentry.captureException(error, {
            level: 'error',
            tags: { operation: 'stop_recording' },
          });
          throw error;
        }
      }
    );
  };

  const handleCreateAINote = async () => {
    if (!recording.recordedBlob) return;

    await Sentry.startSpan(
      {
        op: 'ui.action',
        name: 'Create AI Note',
      },
      async span => {
        try {
          recording.setProcessing();

          span.setAttribute('audio_duration', recording.audioDuration);
          span.setAttribute('audio_size', recording.recordedBlob?.size || 0);
          span.setAttribute('has_patient_id', !!noteContext.patientId);

          const success = await noteContext.createNote(
            recording.recordedBlob!,
            formatDuration(recording.audioDuration),
            (noteId: string) => {
              // Track this as current session note and mark history for refresh
              setCurrentSessionNoteId(noteId);
              setShouldRefreshHistory(true);
              // Reset to idle when note is completed
              recording.recordNew();
              span.setAttribute('note_id', noteId);
            }
          );

          span.setAttribute('success', success);

          if (!success) {
            recording.recordNew();
          }
        } catch (error) {
          span.setAttribute('success', false);
          Sentry.captureException(error, {
            level: 'error',
            tags: { operation: 'create_ai_note' },
            contexts: {
              audio: {
                duration: recording.audioDuration,
                size: recording.recordedBlob?.size,
              },
            },
          });
          recording.recordNew();
          throw error;
        }
      }
    );
  };

  const renderCurrentState = () => {
    switch (recording.recordingState) {
      case 'recording':
      case 'paused':
        return (
          <RecordingStateComponent
            title={title}
            subtitle={subtitle}
            recordingState={recording.recordingState}
            recordingTime={recording.recordingTime}
            onStopRecording={handleStopRecording}
            onPauseResume={recording.pauseResume}
            volumeLevel={recording.volumeLevel}
          />
        );
      case 'stopped':
      case 'processing':
        return (
          <StoppedState
            title={title}
            subtitle={subtitle}
            recordingState={recording.recordingState}
            recordedBlob={recording.recordedBlob}
            isProcessingAudio={isProcessingAudio}
            audioSrc={audioSrc}
            audioDuration={recording.audioDuration}
            currentTime={audioPlayback.currentTime}
            isPlaying={audioPlayback.isPlaying}
            isReady={audioPlayback.isReady}
            audioElement={audioPlayback.audioElement}
            onCreateAINote={handleCreateAINote}
            onRecordNew={handleRecordNew}
            onSeek={audioPlayback.seekTo}
            onPlayPause={audioPlayback.playPause}
            onPlayAgain={audioPlayback.playAgain}
            allowParallelRecording={allowParallelRecording}
          />
        );
      default:
        return (
          <InitialState
            title={title}
            subtitle={subtitle}
            onStartRecording={handleStartRecording}
            disabled={noteContext.isSessionLoading && !allowParallelRecording}
            onEnsureMicPermission={async () => {
              const res = await recording.getMicPermission(true);
              return res.ok;
            }}
            micError={recording.micError}
            selectedDeviceId={recording.selectedDeviceId}
            onDeviceChange={recording.setSelectedDeviceId}
          />
        );
    }
  };

  return (
    <Box sx={audioCardStyles.container}>
      {renderCurrentState()}

      {noteContext.sessionStatus === 'error' && (
        <Box sx={{ mt: 'auto', mb: 2 }}>
          <Typography variant="body2" sx={audioCardStyles.errorContainer}>
            {noteContext.error ??
              'Unable to process audio: The recording appears to be silent or corrupted. Please record a new audio file with clear speech.'}
          </Typography>
        </Box>
      )}

      {silenceDetection.showSilenceWarning && (
        <SilenceWarningPopup onDismiss={silenceDetection.dismissWarning} />
      )}
    </Box>
  );
};

export { AudioStartCard };
